import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ClipboardList, ArrowLeft, Eye } from "lucide-react"
import { externalEntityService } from "../../services/externalEntityService"
import Alert from "../common/Alert"
import {
  PageLayout,
  Button,
  DataTable,
  DetailCard,
  DetailRow,
  StatusBadge,
} from "../common/index.js"

const ViewExternalML = () => {
  const { externalMLId } = useParams()
  const navigate = useNavigate()
  const [mlSheet, setMlSheet] = useState(null)
  const [shipments, setShipments] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchExternalML()
  }, [externalMLId])

  const fetchExternalML = async () => {
    try {
      setLoading(true)
      // GET /api/external-entities/ml-sheets/<external_ml_id>
      // Response: { success, ml_sheet: { ...fields, shipments: [...] } }
      const response = await externalEntityService.getExternalML(externalMLId)
      const ml = response.data.ml_sheet

      if (!ml) throw new Error("No data returned")

      setMlSheet(ml)
      // shipments are embedded directly in the ml_sheet object
      setShipments(Array.isArray(ml.shipments) ? ml.shipments : [])
    } catch (error) {
      console.error("Error fetching External ML:", error)
      Alert.error(error.response?.data?.message || "Failed to load External ML Sheet")
      navigate("/external-ml")
    } finally {
      setLoading(false)
    }
  }

  const getPaymentModeVariant = (mode) => {
    const map = {
      to_pay: "warning",
      paid: "success",
      paid_balance: "info",
    }
    return map[(mode || "").toLowerCase()] || "default"
  }

  if (loading) {
    return (
      <PageLayout title="External ML Sheet" icon={ClipboardList}>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600 font-semibold">Loading External ML Sheet...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!mlSheet) {
    return (
      <PageLayout title="External ML Sheet" icon={ClipboardList}>
        <DetailCard>
          <p className="text-center text-gray-600 text-lg font-semibold">
            External ML Sheet not found
          </p>
          <div className="mt-4 flex justify-center">
            <Button onClick={() => navigate("/external-ml")}>
              Back to External ML Sheets
            </Button>
          </div>
        </DetailCard>
      </PageLayout>
    )
  }

  const shipmentColumns = [
    {
      key: "sr_no",
      label: "#",
      width: "50px",
      render: (_, index) => index + 1,
    },
    {
      key: "external_shipment_id",
      label: "Shipment ID",
      render: (s) => (
        <button
          onClick={() =>
            navigate(`/external-ml/${externalMLId}/shipment/${s.external_shipment_id}`)
          }
          className="font-medium text-brand hover:underline"
        >
          {s.external_shipment_id}
        </button>
      ),
    },
    {
      key: "lr_no",
      label: "LR No",
      render: (s) => s.lr_no || "—",
    },
    {
      key: "consignor",
      label: "Consignor",
      render: (s) => s.consignor || "—",
    },
    {
      key: "consignee",
      label: "Consignee",
      render: (s) => s.consignee || "—",
    },
    {
      key: "to_station",
      label: "To Station",
      render: (s) => s.to_station || "—",
    },
    {
      key: "total_amount",
      label: "Total",
      render: (s) => `₹${(s.total_amount || 0).toFixed(2)}`,
    },
    {
      key: "balance_amount",
      label: "Balance",
      cellClassName: "text-blue-600 font-semibold",
      render: (s) => `₹${(s.balance_amount || 0).toFixed(2)}`,
    },
    {
      key: "payment_mode",
      label: "Payment Mode",
      render: (s) => (
        <StatusBadge
          status={s.payment_mode}
          variant={getPaymentModeVariant(s.payment_mode)}
          label={s.payment_mode || "—"}
        />
      ),
    },
  ]

  const getShipmentActions = (s) => [
    {
      label: "View",
      icon: Eye,
      onClick: () =>
        navigate(`/external-ml/${externalMLId}/shipment/${s.external_shipment_id}`),
    },
  ]

  return (
    <PageLayout
      title={`External ML #${mlSheet.external_ml_id}`}
      icon={ClipboardList}
      actions={
        <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/external-ml")}>
          Back
        </Button>
      }
    >
      <p className="text-tertiary text-sm sm:text-base mb-6">
        {mlSheet.trip_sheet_no ? `Trip Sheet: ${mlSheet.trip_sheet_no}` : ""}
        {mlSheet.created_at
          ? ` · Created ${new Date(mlSheet.created_at).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}`
          : ""}
      </p>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-sm shadow-md p-4 border-l-4 border-brand">
          <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Total To Pay</div>
          <div className="text-xl font-bold text-secondary">
            ₹{(mlSheet.total_topay || 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-white rounded-sm shadow-md p-4 border-l-4 border-brand">
          <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Shipments</div>
          <div className="text-xl font-bold text-secondary">
            {mlSheet.external_shipment_count || shipments.length}
          </div>
        </div>
        <div className="bg-white rounded-sm shadow-md p-4 border-l-4 border-purple-500">
          <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Driver</div>
          <div className="text-sm font-semibold text-secondary break-all">
            {mlSheet.driver_name || "—"}
          </div>
          {mlSheet.driver_contact && (
            <div className="text-xs text-gray-500">{mlSheet.driver_contact}</div>
          )}
        </div>
        <div className="bg-white rounded-sm shadow-md p-4 border-l-4 border-green-500">
          <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Trip Sheet No</div>
          <div className="text-sm font-semibold text-secondary">
            {mlSheet.trip_sheet_no || "—"}
          </div>
        </div>
      </div>

      <DetailCard title="ML Sheet Details" className="mb-6">
        <DetailRow label="ML Sheet ID" value={mlSheet.external_ml_id} />
        <DetailRow label="Trip Sheet No" value={mlSheet.trip_sheet_no || "—"} />
        <DetailRow label="Driver Name" value={mlSheet.driver_name || "—"} />
        <DetailRow label="Driver Contact" value={mlSheet.driver_contact || "—"} />
        <DetailRow label="Created By" value={mlSheet.created_by || "—"} />
        <DetailRow
          label="Created At"
          value={
            mlSheet.created_at
              ? new Date(mlSheet.created_at).toLocaleString("en-IN")
              : "—"
          }
        />
      </DetailCard>

      <DetailCard title={`External Shipments (${shipments.length})`} className="mb-6">
        <DataTable
          columns={shipmentColumns}
          data={shipments}
          emptyMessage="No shipments found"
          rowKey="external_shipment_id"
          actions={getShipmentActions}
          className="shadow-none rounded-none"
        />
      </DetailCard>
    </PageLayout>
  )
}

export default ViewExternalML
